import React, { useEffect, useState } from "react";
import Button from "../../common/Button/Button";
import Select from "../../common/Select/Select"
import api from "../../../services/api"
import { toast, ToastContainer } from "react-toastify";
import styles from "../../../pages/Grievances/AddGrievances.module.css";

const AddGrievanceResponse = ({ complaintId, onSubmitted, onCancel }) => {

    const [learningModules, setLearningModules] = useState([]);
    const [chapters, setChapters] = useState([]);
    const [formData, setFormData] = useState({
        response: "",
        complaintstatus: "",
        closuretext: "",
        attachment: null,
    });
    
    // Notification channel state (inline on form, both pre-selected)
    const [channels, setChannels] = useState({ push: true, email: true });
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        api.get('/learningmodules')
            .then((response) => {
                setLearningModules(response.data);
            })
            .catch((error) => {
                console.error('Error fetching learning modules:', error);
            });
    }, []);

    useEffect(() => {
        api.get('/chaptermaster')
            .then((response) => {
                setChapters(response.data);
            })
            .catch((error) => {
                console.error('Error fetching chapter data:', error);
            });
    }, []);

    const handleChange = (e) => {
        const { name, value, files } = e.target;

        if (name !== "attachment") {
            setFormData((prev) => ({ ...prev, [name]: value }));
            return;
        }

        const file = files?.[0];
        if (!file) return;

        const MAX_SIZE = 5 * 1024 * 1024; // 5MB

        const ext = file.name.split(".").pop().toLowerCase();

        const allowedExtensions = [
            "jpg", "jpeg", "png", "gif", "webp",
            "pdf",
            "txt",
            "doc", "docx",
            "zip", "rar"
        ];

        if (file.size > MAX_SIZE) {
            toast.error("❌ File size must be under 5MB");
            e.target.value = "";
            return;
        }

        if (!allowedExtensions.includes(ext)) {
            toast.error("❌ Unsupported file type");
            e.target.value = "";
            return;
        }

        setFormData((prev) => ({ ...prev, [name]: file }));
    };

    const handleClearAttachment = () => {
        setFormData((prev) => ({ ...prev, attachment: null }));
        document.getElementById("attachmentInput").value = "";
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate at least one notification channel
        if (!channels.push && !channels.email) {
            toast.error("At least one notification channel must be selected.");
            return;
        }

        setSubmitting(true);
        const userId = localStorage.getItem("userId");
        let userName = "";
        try {
            const userRes = await api.get(`/users/${userId}`);
            userName = userRes.data?.name || userRes.name || "";
        } catch (e) {
            console.error("Error fetching user name:", e);
        }

        const form = new FormData();
        Object.entries(formData).forEach(([key, value]) => value && form.append(key, value));

        form.append("userId", userId);
        const now = new Date();
        const responseDate = now.toISOString().split("T")[0];
        const closedDate = now.toISOString().split("T")[0];
        const responseTime = now.toTimeString().split(" ")[0];
        const closedTime = now.toTimeString().split(" ")[0];

        form.append("responseDate", responseDate);
        form.append("responseTime", responseTime);
        form.append("closedDate", closedDate);
        form.append("closedTime", closedTime);
        form.append("respondedBy", userName);
        form.append("closedBy", userName);
        form.append("complaintId", complaintId);

        const selectedChannels = [];
        if (channels.push) selectedChannels.push("push");
        if (channels.email) selectedChannels.push("email");
        
        form.append("channels", JSON.stringify(selectedChannels));

        try {
            const res = await api.post(`/grievanceresponse`, form);
            // api.js response interceptor returns response.data directly
            setResult(res);
        } catch (error) {
            console.error("Error:", error);
            toast.error("Failed to submit response!");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div>
            <ToastContainer position="bottom-right" autoClose={2000} style={{ zIndex: 999999 }} />
            
            {!result ? (
            <form onSubmit={handleSubmit}>

                {/* Response Textarea (Full Width) */}
                <div className={styles.inputGroupFull} style={{ marginBottom: '14px' }}>
                    <label>Response</label>
                    <textarea
                        type="text"
                        name="response"
                        value={formData.response}
                        onChange={handleChange}
                        required
                    />
                </div>

                {/* Row for Select and Closure Text */}
                <div className={styles.row}>
                    <div className={styles.inputGroup}>
                        <label>Complaint Status</label>
                        <Select
                            required
                            placeholder="Select Status"
                            name="complaintstatus"
                            value={formData.complaintstatus || ""}
                            onChange={handleChange}
                            options={[
                                { value: "Opened", label: "Opened" },
                                { value: "Closed", label: "Closed" }
                            ]}
                        />
                    </div>
                    
                    <div className={styles.inputGroup}>
                        <label>Closure Text</label>
                        <input
                            type="text"
                            name="closuretext"
                            value={formData.closuretext}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    {/* Notification Checkboxes (inline in the row) */}
                    <div className={styles.inputGroup}>
                        <label>Notify User</label>
                        <div className={styles.notificationOptions}>
                            <label className={styles.checkboxLabel}>
                                <input 
                                    type="checkbox" 
                                    checked={channels.push}
                                    onChange={(e) => setChannels(prev => ({ ...prev, push: e.target.checked }))}
                                />
                                Push Notification
                            </label>
                            <label className={styles.checkboxLabel}>
                                <input 
                                    type="checkbox" 
                                    checked={channels.email}
                                    onChange={(e) => setChannels(prev => ({ ...prev, email: e.target.checked }))}
                                />
                                Email Notification
                            </label>
                        </div>
                    </div>
                </div>

                {/* Attachment (Full Width Below) */}
                <div className={styles.inputGroupFull} style={{ marginBottom: '14px' }}>
                    <label className="optional">
                        Attachment (Optional)
                        <span style={{ fontSize: "12px", color: "#FF0000" }}>
                            {" "} (Max 5MB • Image, PDF, Txt, Word, Zip, Rar)
                        </span>
                    </label>

                    <input
                        id="attachmentInput"
                        type="file"
                        name="attachment"
                        onChange={handleChange}
                        accept="
                            image/*,
                            application/pdf,
                            text/plain,
                            application/msword,
                            application/vnd.openxmlformats-officedocument.wordprocessingml.document,
                            .zip,
                            .rar
                        "
                    />
                    {formData.attachment && (
                        <div className={styles.attachmentPreview}>
                            <span>{formData.attachment.name}</span>
                            <button type="button" onClick={handleClearAttachment}>
                                Clear
                            </button>
                        </div>
                    )}
                </div>

                <div className={styles.buttonGroup}>
                    <button type="submit" className={styles.submitBtn} disabled={submitting}>
                        {submitting ? "Sending..." : "Submit"}
                    </button>
                    <button type="button" onClick={onCancel} className={styles.backBtn} disabled={submitting}>
                        Cancel
                    </button>
                </div>

            </form>
            ) : (
                <div className={styles.resultCard}>
                    <div className={styles.resultTitle}>Response Result</div>
                    <div className={styles.resultList}>
                        <div className={styles.resultItem}>
                            <span className={styles.resultSuccess}>✓</span> Grievance response saved successfully.
                        </div>
                        
                        {result.notifications?.push && (
                            <div className={styles.resultItem}>
                                {result.notifications.push.success ? (
                                    <span className={styles.resultSuccess}>✓</span>
                                ) : (
                                    <span className={styles.resultError}>✗</span>
                                )} 
                                Push notification {result.notifications.push.success ? 'sent successfully.' : `failed: ${result.notifications.push.message}`}
                            </div>
                        )}

                        {result.notifications?.email && (
                            <div className={styles.resultItem}>
                                {result.notifications.email.success ? (
                                    <span className={styles.resultSuccess}>✓</span>
                                ) : (
                                    <span className={styles.resultError}>✗</span>
                                )} 
                                Email notification {result.notifications.email.success ? 'sent successfully.' : `failed: ${result.notifications.email.message}`}
                            </div>
                        )}

                        {result.notifications?.archive && (
                            <div className={styles.resultItem}>
                                {result.notifications.archive.success ? (
                                    <span className={styles.resultSuccess}>✓</span>
                                ) : (
                                    <span className={styles.resultError}>✗</span>
                                )} 
                                {result.notifications.archive.message || 'Response archived successfully.'}
                            </div>
                        )}
                    </div>
                    <button 
                        type="button" 
                        className={`${styles.submitBtn} ${styles.resultCloseBtn}`}
                        onClick={() => {
                            if (onSubmitted) onSubmitted();
                        }}
                    >
                        Close
                    </button>
                </div>
            )}

        </div>
    );
};

export default AddGrievanceResponse;
